import {
  getImageUrl,
  isMediaServiceImageUrl,
  isTypedPicture,
  isVtpImageUrl,
  Picture,
  RenderedMarkerPLPPDP,
  useDynamicAltText,
  VehicleBasic,
  vtpImageUrlAdaption,
} from '@oneaudi/vtp-shared';
import React, { useEffect, useState } from 'react';
import { ImageContainer, StyledPicture, StyledImage } from './GalleryImage.styles';
import { SYS_SIZE_FIXED_300 } from '@oneaudi/unified-web-common';

export interface GalleryImageProps {
  vehicle: VehicleBasic;
  picture: Picture;
  active?: boolean;
  // this prop is intended as a manual override in cases the native lazy loading does not bring the desired effect.
  // Picture that are not rendered initially will be loaded eagerly once this prop is set to true later
  renderPicture?: boolean;
  isSinglePicture?: boolean;
}

const GalleryImage: React.FC<GalleryImageProps> = ({
  vehicle,
  picture,
  active,
  renderPicture: renderPictureFromProps = true,
  isSinglePicture,
}) => {
  const [renderPicture, setRenderPicture] = useState(renderPictureFromProps);
  const [loading, setLoading] = useState<'lazy' | 'eager'>('lazy');

  const isVtpImage = isVtpImageUrl(picture.url);
  const imageUrl = isVtpImage ? vtpImageUrlAdaption(picture.url, 600, 450) : picture.url;

  const isMediaServiceImage = isMediaServiceImageUrl(picture.url);

  const fallbackSrc = isMediaServiceImage
    ? getImageUrl(picture.url, 600, undefined, 'image/webp')
    : imageUrl;

  const altForImage = useDynamicAltText(picture.key, vehicle);

  useEffect(() => {
    if (renderPictureFromProps && !renderPicture) {
      setRenderPicture(true);
      setLoading('eager');
    }
  }, [renderPictureFromProps]);

  return (
    <ImageContainer
      photoImage={isTypedPicture(picture) && picture.type !== 'render'}
      data-testid="imageContainer"
      isSinglePicture={isSinglePicture}
    >
      {renderPicture && (
        <StyledPicture>
          {isMediaServiceImage && (
            <>
              <source
                srcSet={`${getImageUrl(picture.url, 600, undefined, 'image/webp')} 1x, ${getImageUrl(picture.url, 800, undefined, 'image/webp')} 2x`}
                media="(max-width: 567px)"
                type="image/webp"
              />
              <source
                srcSet={getImageUrl(picture.url, 800, undefined, 'image/webp')}
                media="(min-width: 568px)"
                type="image/webp"
              />
            </>
          )}
          {isVtpImage && (
            <>
              <source srcSet={vtpImageUrlAdaption(picture.url, 800, 450)} />
            </>
          )}
          <StyledImage
            src={fallbackSrc}
            alt={altForImage}
            data-testid="image"
            loading={loading}
            isSinglePicture={isSinglePicture}
          />
        </StyledPicture>
      )}
      {active && (
        <RenderedMarkerPLPPDP
          picture={picture}
          data-testid="renderedMarker"
          padding="0"
          top={SYS_SIZE_FIXED_300}
          left={SYS_SIZE_FIXED_300}
        />
      )}
    </ImageContainer>
  );
};

export default GalleryImage;
